// Progress: coverage per skill against everything that exists, then the
// specific task types costing the most points, each with its fix one click
// away. Numbers are facts; the page exists to point, not to decorate.
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

import { api } from '@/lib/api';
import AppShell from '@/components/AppShell';
import { cn } from '@/lib/utils';

const SECTION_ORDER = {
  celpip: ['listening', 'reading', 'writing', 'speaking'],
  pte: ['speaking', 'writing', 'reading', 'listening'],
};

export default function ProgressPage() {
  const { exam } = useParams();
  const [progress, setProgress] = useState(null);
  const [coverage, setCoverage] = useState([]);
  const [units, setUnits] = useState([]);
  const [traps, setTraps] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const [p, c, u, t] = await Promise.all([
          api.progress(exam), api.coverage(exam), api.curriculum(exam), api.repeatedTraps(exam),
        ]);
        setProgress(p.data.progress);
        setCoverage(c.data.coverage);
        setUnits(u.data.units);
        setTraps(t.data.traps || []);
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to load progress.');
      }
    };
    load();
  }, [exam]);

  const titleFor = useMemo(() => {
    const map = {};
    units.forEach((u) => { if (u.task_type) map[u.task_type] = u.title; });
    return (tt) => map[tt] || (tt ? tt.replace(/_/g, ' ') : 'Mock section');
  }, [units]);

  if (error) return (
    <AppShell>
      <p className="text-slate-600">{error}</p>
      <button type="button" onClick={() => window.location.reload()} className="cta mt-4">Try again</button>
    </AppShell>
  );
  if (!progress) return <AppShell><p className="text-slate-400">Loading…</p></AppShell>;

  const order = SECTION_ORDER[exam] || SECTION_ORDER.celpip;
  const bySection = order.map((section) => {
    const av = coverage.filter((c) => c.section === section);
    const done = progress.filter((p) => p.section === section);
    const auto = done.filter((p) => p.accuracy != null);
    const acc = auto.length ? auto.reduce((a, r) => a + r.accuracy, 0) / auto.length : null;
    return {
      section,
      available: av.reduce((a, r) => a + r.sets, 0),
      attempted: done.reduce((a, r) => a + r.attempts, 0),
      accuracy: acc,
    };
  }).filter((s) => s.available > 0);

  const weakest = progress
    .filter((p) => p.task_type && p.accuracy != null && p.attempts > 0 && p.accuracy < 0.75)
    .sort((a, b) => a.accuracy - b.accuracy)
    .slice(0, 5);

  const totalAttempted = progress.reduce((a, r) => a + r.attempts, 0);

  return (
    <AppShell>
      <p className="mono-stat">{exam} · progress</p>
      <h1 className="h-display mt-2 text-3xl" data-testid="progress-hero">Where you stand.</h1>
      <p className="mt-2 text-sm text-slate-500">{totalAttempted} sets and tasks completed so far.</p>

      <section className="mt-10">
        <div className="flex items-baseline justify-between border-b-2 border-slate-900 pb-2">
          <h2 className="h-display text-xl">Coverage by skill</h2>
          <span className="text-xs text-slate-400">attempted of available</span>
        </div>
        <div className="border border-t-0 border-slate-200 bg-white">
          {bySection.map((s, i) => {
            const ratio = s.available ? Math.min(1, s.attempted / s.available) : 0;
            const pct = s.accuracy != null ? Math.round(s.accuracy * 100) : null;
            return (
              <div key={s.section} className={cn('flex items-center gap-6 px-6 py-5', i > 0 && 'border-t border-slate-100')}>
                <span className="w-24 shrink-0 text-sm font-semibold capitalize text-slate-900">{s.section}</span>
                <div className="h-1 flex-1 bg-slate-100">
                  <div className="h-1 bg-indigo-600" style={{ width: `${ratio * 100}%` }} />
                </div>
                <span className="w-28 shrink-0 text-right text-sm tabular-nums text-slate-500" data-testid={`coverage-${s.section}`}>
                  {s.attempted} / {s.available} sets
                </span>
                <span className={cn('h-display w-16 shrink-0 text-right text-xl tabular-nums', pct == null ? 'text-slate-200' : pct >= 75 ? 'text-emerald-600' : 'text-indigo-600')}>
                  {pct == null ? '-' : `${pct}%`}
                </span>
              </div>
            );
          })}
        </div>
      </section>

      <section className="mt-12">
        <div className="flex items-baseline justify-between border-b-2 border-slate-900 pb-2">
          <h2 className="h-display text-xl">Costing you the most</h2>
          <span className="text-xs text-slate-400">lowest accuracy first</span>
        </div>
        {weakest.length === 0 ? (
          <p className="mt-4 text-sm text-slate-500">
            {progress.some((p) => p.attempts > 0)
              ? 'Nothing measured under 75% right now. Keep widening coverage.'
              : 'Complete a few timed sets and this section starts pointing at what to fix first.'}
          </p>
        ) : (
          <div className="border border-t-0 border-slate-200 bg-white">
            {weakest.map((w, i) => {
              const pct = Math.round(w.accuracy * 100);
              return (
                <div key={w.task_type} className={cn('flex items-center gap-6 px-6 py-4', i > 0 && 'border-t border-slate-100')}>
                  <span className="w-7 shrink-0 text-xs font-medium tabular-nums text-slate-300">{String(i + 1).padStart(2, '0')}</span>
                  <div className="min-w-0 flex-1">
                    <p className="font-display text-[15px] font-semibold text-slate-900">{titleFor(w.task_type)}</p>
                    <p className="text-xs capitalize text-slate-400">{w.section} · {w.attempts} attempt{w.attempts === 1 ? '' : 's'}</p>
                  </div>
                  <span className="h-display w-14 shrink-0 text-right text-xl tabular-nums text-red-600">{pct}%</span>
                  <Link
                    to={`/${exam}/sets/${w.task_type}`}
                    className="inline-flex shrink-0 items-center gap-1.5 text-sm font-semibold text-indigo-600 hover:text-indigo-800"
                    data-testid={`weak-fix-${w.task_type}`}
                  >
                    Train it <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {traps.length > 0 && (
        <section className="mt-12">
          <div className="flex items-baseline justify-between border-b-2 border-slate-900 pb-2">
            <h2 className="h-display text-xl">Traps you keep falling for</h2>
            <span className="text-xs text-slate-400">most frequent first</span>
          </div>
          <div className="border border-t-0 border-slate-200 bg-white">
            {traps.map((t, i) => (
              <div key={t.type} className={cn('px-6 py-4', i > 0 && 'border-t border-slate-100')} data-testid={`trap-${t.type}`}>
                <div className="flex items-center gap-4">
                  <span className="flex h-6 min-w-6 items-center justify-center rounded bg-rose-100 px-1.5 text-xs font-bold tabular-nums text-rose-700">{t.count}×</span>
                  <p className="flex-1 font-display text-[15px] font-semibold text-slate-900">{t.label}</p>
                  <span className="shrink-0 text-xs tabular-nums text-slate-400">{Math.round(t.share * 100)}% of your slips</span>
                </div>
                {t.coaching && <p className="mt-1.5 pl-10 text-sm leading-6 text-slate-600">{t.coaching}</p>}
                {t.top_tasks?.length > 0 && (
                  <p className="mt-1 pl-10 text-xs capitalize text-slate-400">Shows up most in: {t.top_tasks.map((tt) => titleFor(tt)).join(', ')}</p>
                )}
              </div>
            ))}
          </div>
          <p className="mt-2 text-xs text-slate-400">Counts only wrong answers where the trap is confidently identified, so this points at a real pattern, not a guess.</p>
        </section>
      )}
    </AppShell>
  );
}
